const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const { actionUtils } = require('@sstdev/lib_metadata-config');
const { config } = require('@sstdev/lib_metadata-config');
const { merge } = require('lodash');
const dataAccessMethods = require('../../../../dataAccessMethods');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP = 'Provide button to export specific columns based on current grid`s filter';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;

    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);

    line1OfInputs
        .appendField('  Namespace:')
        .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
        .appendField('  Relation:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)), 'relation');

    // Load possible actions and default action
    const actionsDropdown = new Blockly.FieldDropdown(config.actionElementActions);
    actionsDropdown.setValue('export');
    line1OfInputs
        //should we even include this option? it should always be export
        .appendField('  For Action:')
        .appendField(actionsDropdown, 'forAction')
        .appendField('  File Prefix:')
        .appendField(new Blockly.FieldTextInput(''), 'filePrefix')
        .appendField('  Allow export type selection:')
        .appendField(new Blockly.FieldCheckbox(false), 'includeExportType');

    // Set button style
    let styleDropDown = new Blockly.FieldDropdown(config.buttonStyles);
    styleDropDown.setValue('IconAction');
    const line2OfInput = blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Button Style (old):')
        .appendField(styleDropDown, 'buttonStyle')
        .appendField('  Button Size (new):')
        .appendField(new Blockly.FieldDropdown(config.componentSize), 'size')
        .appendField('  Button Variant (new):')
        .appendField(new Blockly.FieldDropdown(config.buttonVariants), 'variant')
        .appendField('  Icon Name:')
        .appendField(new Blockly.FieldTextInput('cloud_download'), 'iconName')
        .appendField('  Busy Animation Until Finished')
        .appendField(new Blockly.FieldCheckbox(), 'showBusyUntilFinished');

    line2OfInput
        .appendField('  Shortcut')
        .appendField(new Blockly.FieldTextInput(), 'shortcutKey')
        .appendField('  Shift Button')
        .appendField(new Blockly.FieldCheckbox(), 'shiftButton')
        .appendField('  Control Button')
        .appendField(new Blockly.FieldCheckbox(), 'controlButton')
        .appendField('  Alt Button')
        .appendField(new Blockly.FieldCheckbox(), 'altButton');

    blocklyBlock.appendStatementInput('columns').setCheck(['listColumn']).appendField('List Columns:');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation']);

    //action
    addCodeProperties(['forAction', 'filePrefix', { key: 'includeExportType', handler: convertToBoolean }]);

    //Button Display
    addCodeProperties([{ key: 'style', value: 'buttonStyle' }, 'size', 'variant', 'iconName']);
    addCodeProperties([{ key: 'showBusyUntilFinished', handler: convertToBoolean }]);

    //shortcut
    addCodeProperties([
        'shortcutKey',
        { key: 'shiftButton', handler: convertToBoolean },
        { key: 'controlButton', handler: convertToBoolean },
        { key: 'altButton', handler: convertToBoolean }
    ]);

    return code;
}

/**
 * Builds relation metadata for the DownloadButton action.
 * This is an export/view-only block that describes columns for data export.
 * It should NOT define canonical relation schemas - those come from Forms.
 * Export views only describe available data for download, not input requirements.
 */
function asDataDictionary(hNode, context, parentAccessRestrictions) {
    const { forAction, children = [] } = hNode;

    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);

    if (accessRestrictions.hidden) return [{}, {}];

    const accessContext = {
        get: { value: true, observedLocation: hNode.blockId },
        post: { value: true, observedLocation: hNode.blockId },
        patch: { value: false, observedLocation: hNode.blockId },
        put: { value: false, observedLocation: hNode.blockId },
        remove: { value: false, observedLocation: hNode.blockId }
    };

    const normalizedAction = actionUtils.normalizeAction(forAction);
    const targetRelationSchema = {
        report: {
            generate: Type.Object(
                {},
                {
                    // DownloadButton grants report.generate access when the button is visible because users who can
                    // access a cached-online download button should always be able to use server-side export.
                    // This is a synthetic access shell for report.generate, not a canonical input schema.
                    title: 'generate',
                    additionalProperties: true,
                    ...(normalizedAction ? { 'x-forAction': normalizedAction } : {}),
                    'x-accessRestrictions': { ...accessRestrictions, readOnly: false },
                    'x-accessContext': accessContext
                }
            )
        }
    };

    // Aggregate child contributions (list columns for export)
    let mergedChildSubDD = {};
    let mergedChildPartialMap = {};

    for (const child of children) {
        const [cSub = {}, cPartial = {}] = context.extractFromChild(child, context, accessRestrictions) || [{}, {}];
        mergedChildSubDD = merge(mergedChildSubDD, cSub);
        mergedChildPartialMap = Object.assign(mergedChildPartialMap, cPartial);
    }

    // View-only block: only pass through child subDataDictionary contributions.
    // Do NOT create or merge into the canonical [namespace][relation] slot.
    // Canonical schemas are defined by Form blocks.
    // Export column definitions are for UI/export purposes only.
    return [merge(targetRelationSchema, mergedChildSubDD), mergedChildPartialMap];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
